// Pure, DOM-free replay engine for a keystroke-timed terminal recording.
//
// It consumes an asciinema v2 superset — a header object plus event tuples of
// the form [time_seconds, "o", "output text"] — with one authored extension:
// the "gap" event [time_seconds, "gap", seconds], which marks a long real wait
// (e.g. a 90-second hang) that playback compresses into a short, clearly
// labelled beat with a skippable pause, so the reader feels the hang without
// waiting the full 90 seconds.
//
// There is no DOM access and no globals here. Rendering happens through an
// injected writer (write / clear) and timing through an injected scheduler,
// which keeps the whole engine deterministic and unit-testable. All
// interpolated text is HTML-escaped before it reaches the writer.
//
// EMBED CONSTRAINT (reuse note): when a cast is embedded in an article content
// block, that block's HTML is Liquid-rendered before it reaches the browser, so
// cast output text must NOT contain the literal sequences "{{" or "{%" — Liquid
// would delete "{{ ... }}" and 500 the page on "{% ... %}". Wrapping in
// {% raw %} does not help (a compile-time Liquid pass strips it). The wave0
// loader (load_posts.rb) fails fast on such tokens; see guard_liquid_safe!.

export function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

// Human-readable duration for a gap marker: whole minutes collapse to "Nm",
// whole seconds to "Ns", otherwise one decimal ("0.3s").
export function formatSeconds(seconds) {
  const n = Number(seconds)
  if (!Number.isFinite(n)) return "0s"
  if (n >= 60 && n % 60 === 0) return `${n / 60}m`
  return `${n % 1 === 0 ? n : n.toFixed(1)}s`
}

const DEFAULT_SCHEDULER = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id)
}

// Normalises a raw cast (object or JSON string) into
// { version, width, height, title, events: [{ time, type, text?, seconds? }] }.
// Throws a descriptive Error on anything it can't read; callers that need
// tolerance wrap this in try/catch and no-op.
export function normalizeCast(raw) {
  const cast = typeof raw === "string" ? JSON.parse(raw) : raw
  if (!cast || typeof cast !== "object" || Array.isArray(cast)) {
    throw new Error("cast must be an object")
  }
  if (!Array.isArray(cast.events)) {
    throw new Error("cast.events must be an array")
  }

  const events = cast.events.map((event, i) => normalizeEvent(event, i))
  return {
    version: Number(cast.version) || 2,
    width: Number(cast.width) || 80,
    height: Number(cast.height) || 24,
    title: cast.title == null ? "" : String(cast.title),
    events
  }
}

function normalizeEvent(event, i) {
  if (!Array.isArray(event) || event.length < 3) {
    throw new Error(`event ${i} must be a [time, type, payload] tuple`)
  }
  const time = Number(event[0])
  const type = event[1]
  if (!Number.isFinite(time)) throw new Error(`event ${i} has a non-numeric time`)
  if (type === "o") {
    return { time, type: "o", text: String(event[2]) }
  }
  if (type === "gap") {
    const seconds = Number(event[2])
    if (!Number.isFinite(seconds)) throw new Error(`gap event ${i} needs numeric seconds`)
    return { time, type: "gap", seconds }
  }
  throw new Error(`event ${i} has an unsupported type: ${String(type)}`)
}

export class TerminalReplay {
  constructor({
    cast,
    write,
    clear,
    scheduler,
    onState,
    gapBeatMs = 2400,
    maxStepMs = 1200
  } = {}) {
    this.cast = normalizeCast(cast)
    this._write = typeof write === "function" ? write : () => {}
    this._clear = typeof clear === "function" ? clear : () => {}
    this._scheduler = scheduler || DEFAULT_SCHEDULER
    this._onState = typeof onState === "function" ? onState : () => {}
    this._gapBeatMs = gapBeatMs
    this._maxStepMs = maxStepMs
    this._index = 0 // index of the next event to render
    this._status = "idle" // idle | playing | gap | paused | done
    this._timer = null
  }

  get events() { return this.cast.events }
  get total() { return this.cast.events.length }
  get index() { return this._index }
  get status() { return this._status }
  get gapActive() { return this._status === "gap" }

  // Begin (or resume) playback. From "idle" it first clears the server-rendered
  // fallback transcript so the replay starts from a clean surface.
  play() {
    if (this._status === "playing" || this._status === "gap" || this._status === "done") return
    if (this._status === "idle") this._clear()
    this._setStatus("playing")
    this._schedule(0)
  }

  pause() {
    this._cancelTimer()
    if (this._status !== "done" && this._status !== "idle") this._setStatus("paused")
  }

  restart() {
    this._cancelTimer()
    this._clear()
    this._index = 0
    this._setStatus("playing")
    this._schedule(0)
  }

  // Collapse the compressed gap beat immediately and continue.
  skipGap() {
    if (this._status !== "gap") return
    this._cancelTimer()
    this._step()
  }

  // Render the whole transcript instantly and finish (reduced-motion path).
  revealAll() {
    this._cancelTimer()
    this._clear()
    for (const event of this.events) this._render(event)
    this._index = this.total
    this._setStatus("done")
  }

  // Jump to event index i: render events 0..i instantly, then hold paused.
  seek(i) {
    if (this.total === 0) return
    const target = Math.max(0, Math.min(this.total - 1, Math.trunc(Number(i))))
    this._cancelTimer()
    this._clear()
    for (let j = 0; j <= target; j++) this._render(this.events[j])
    this._index = target + 1
    this._setStatus(this._index >= this.total ? "done" : "paused")
  }

  _schedule(delay) {
    this._timer = this._scheduler.setTimeout(() => {
      this._timer = null
      this._step()
    }, delay)
  }

  _step() {
    const events = this.events
    if (this._index >= events.length) { this._finish(); return }

    const event = events[this._index]
    this._render(event)
    this._index += 1

    if (this._index >= events.length) { this._finish(); return }

    const isGap = event.type === "gap"
    const nextEvent = events[this._index]
    const delay = isGap
      ? this._gapBeatMs
      : Math.min(this._maxStepMs, Math.max(0, Math.round((nextEvent.time - event.time) * 1000)))
    this._setStatus(isGap ? "gap" : "playing")
    this._schedule(delay)
  }

  _finish() {
    this._cancelTimer()
    this._setStatus("done")
  }

  _render(event) {
    if (event.type === "gap") {
      this._write(this._gapMarkup(event.seconds))
    } else {
      this._write(escapeHtml(event.text))
    }
  }

  _gapMarkup(seconds) {
    const label = escapeHtml(`⏳ ~${formatSeconds(seconds)} of real silence…`)
    return `<span class="terminal-replay__gap">${label}\n</span>`
  }

  _cancelTimer() {
    if (this._timer != null) {
      this._scheduler.clearTimeout(this._timer)
      this._timer = null
    }
  }

  _setStatus(status) {
    this._status = status
    this._onState({
      status,
      index: this._index,
      total: this.total,
      gapActive: status === "gap"
    })
  }
}
